import {useQuery} from '@tanstack/react-query'
import {passengerService} from '@/services/passengerService'

/**
 * Query keys for the booking selectables. Exported so synchronous / non-React
 * readers (e.g. utils/bookingPayload.js, modalPages/orderDetails.jsx) can read
 * the cached lists via queryClient.getQueryData(SELECTABLES_KEYS.x).
 */
export const SELECTABLES_KEYS = {
    passengerTypes: ['selectables', 'passenger-types'],
    childSeatTypes: ['selectables', 'child-seat-types'],
    luggageTypes: ['selectables', 'luggage-types'],
}

// These lists are static reference data — cache them for the whole session and
// never garbage-collect, so the modal flow reads them without refetching. A full
// page reload clears the in-memory cache, so the APIs run again then.
const SELECTABLES_OPTIONS = {
    staleTime: Infinity,
    gcTime: Infinity,
}

export function usePassengerTypes({enabled = true} = {}) {
    return useQuery({
        enabled,
        queryKey: SELECTABLES_KEYS.passengerTypes,
        queryFn: async () => {
            const result = await passengerService.getPassengerTypes()
            if (!result.success) throw new Error(result.error)
            return result.data
        },
        ...SELECTABLES_OPTIONS,
    })
}

export function useChildSeatTypes({enabled = true} = {}) {
    return useQuery({
        enabled,
        queryKey: SELECTABLES_KEYS.childSeatTypes,
        queryFn: async () => {
            const result = await passengerService.getChildSeatTypes()
            if (!result.success) throw new Error(result.error)
            return result.data
        },
        ...SELECTABLES_OPTIONS,
    })
}

export function useLuggageTypes({enabled = true} = {}) {
    return useQuery({
        enabled,
        queryKey: SELECTABLES_KEYS.luggageTypes,
        queryFn: async () => {
            const result = await passengerService.getLuggageTypes()
            if (!result.success) throw new Error(result.error)
            return result.data
        },
        ...SELECTABLES_OPTIONS,
    })
}

export function useSelectables({enabled = true} = {}) {
    const passenger = usePassengerTypes({enabled})
    const childSeat = useChildSeatTypes({enabled})
    const luggage = useLuggageTypes({enabled})

    return {
        passengerTypes: passenger.data || [],
        childSeatTypes: childSeat.data || [],
        luggageTypes: luggage.data || [],
        isLoading: passenger.isLoading || childSeat.isLoading || luggage.isLoading,
        error: passenger.error || childSeat.error || luggage.error,
    }
}
